import type { Catchup, CatchupProgram } from '@setplex/tria-api'
import type {
  CatchupFormatted,
  CatchupProgramFormatted,
  CatchupsRedirectParams,
} from '~/shared/interfaces'

import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { persist } from 'effector-storage/session'
import { reset } from 'patronum'
import { model as i18nModel } from '~/core/i18n'
import { model as router } from '~/core/router'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import * as pinLockFeature from '~/features/pin-lock'
import { noop } from '~/shared/effector'
import { aye, nay } from '~/shared/helpers'
import {
  findCatchupProgramIdByAirTime,
  formatCatchupsDate,
  formatPaidCatchup,
  formatProgramsForSelectedDate,
  lockedFormat,
} from './lib'

// all catchups
const DEFAULT_UNLOCK_ID = 0

export const navigateCatchupPlayer = createEvent<{
  catchupId: number
  channelId: number
  programmeId: number
  routeName?: string
}>()

sample({
  clock: navigateCatchupPlayer,
  fn: ({ catchupId, channelId, programmeId, routeName }) => {
    const query = `catchupId=${catchupId}&channelId=${channelId}&programmeId=${programmeId}`
    return routeName
      ? `/player/catchup?${query}&routeName=${routeName}`
      : `/player/catchup?${query}&categoryName=All`
  },
  target: router.navigatePush,
})

export type CatchupPlaybackFromSearch = {
  channelId: number
  startMs?: number
  stopMs?: number
  routeName?: string
}

type ResolvedCatchupPlayback =
  | {
      kind: 'player'
      catchupId: number
      channelId: number
      programmeId: number
    }
  | { kind: 'details'; catchupId: number; channelId: number }
  | { kind: 'none' }

export const playCatchupFromSearch = createEvent<CatchupPlaybackFromSearch>()

export const resolveCatchupPlaybackFx = attach({
  source: api.catchups.$channelsWithCatchups,
  async effect(
    catchups,
    { channelId, startMs, stopMs }: CatchupPlaybackFromSearch
  ): Promise<ResolvedCatchupPlayback> {
    if (!Number.isFinite(channelId)) return { kind: 'none' }

    let list = catchups
    if (list.length === 0) {
      list = await api.catchups.getChannelsWithCatchupsFx()
    }

    const catchup = list.find((item) => item.channel.id === channelId)
    if (catchup == null) return { kind: 'none' }

    const { programs } = await api.catchups.getChannelCatchupProgramsFx({
      catchupId: catchup.id,
      channelId,
    })
    const programmeId = findCatchupProgramIdByAirTime(programs, {
      startMs,
      stopMs,
    })
    if (programmeId == null) {
      return { kind: 'details', catchupId: catchup.id, channelId }
    }
    return {
      kind: 'player',
      catchupId: catchup.id,
      channelId,
      programmeId,
    }
  },
})

const $queuedCatchupFromSearch = createStore<CatchupPlaybackFromSearch | null>(
  null
).on(playCatchupFromSearch, (_, params) => params)

sample({
  clock: playCatchupFromSearch,
  source: resolveCatchupPlaybackFx.pending,
  filter: (pending) => !pending,
  fn: (_, params) => params,
  target: resolveCatchupPlaybackFx,
})

const isSameCatchupPlayback = (
  left: CatchupPlaybackFromSearch,
  right: CatchupPlaybackFromSearch
) =>
  left.channelId === right.channelId &&
  left.startMs === right.startMs &&
  left.stopMs === right.stopMs

sample({
  clock: resolveCatchupPlaybackFx.finally,
  source: $queuedCatchupFromSearch,
  filter: (queued, { params }) =>
    queued != null && !isSameCatchupPlayback(queued, params),
  fn: (queued) => queued!,
  target: resolveCatchupPlaybackFx,
})

sample({
  clock: resolveCatchupPlaybackFx.done,
  source: $queuedCatchupFromSearch,
  filter: (queued, { params, result }) =>
    result.kind === 'player' &&
    (queued == null || isSameCatchupPlayback(queued, params)),
  fn: (_, { params, result }) =>
    result.kind === 'player'
      ? {
          catchupId: result.catchupId,
          channelId: result.channelId,
          programmeId: result.programmeId,
          routeName: params.routeName,
        }
      : { catchupId: 0, channelId: 0, programmeId: 0 },
  target: navigateCatchupPlayer,
})

sample({
  clock: resolveCatchupPlaybackFx.done,
  source: $queuedCatchupFromSearch,
  filter: (queued, { params, result }) =>
    result.kind === 'details' &&
    (queued == null || isSameCatchupPlayback(queued, params)),
  fn: (_, { result }) =>
    result.kind === 'details'
      ? `/modules/catchups/${result.catchupId}/channel/${result.channelId}`
      : '',
  target: router.navigatePush,
})

export const getAllCatchupsFx = //
  attach({ effect: api.catchups.getChannelsWithCatchupsFx })

export const $allCatchups = combine(
  api.catchups.$channelsWithCatchups,
  ssePurchasesModel.$channel,
  (channels, adjust) =>
    channels.map((catchup) => ({
      ...catchup,
      channel: adjust(catchup.channel),
    }))
)

export const setAllCatchupsToDisplay = createEvent<Catchup[]>()
export const $allCatchupsToDisplay = createStore<Catchup[]>([])
  .on(api.catchups.$channelsWithCatchups, (_, catchups) => catchups)
  .on(setAllCatchupsToDisplay, (_, catchups) => catchups)

export const $allCatchupsToDisplayFormatted = createStore<CatchupFormatted[]>(
  []
).on(
  combine([$allCatchupsToDisplay, ssePurchasesModel.$channel, i18nModel.$t]),
  (_, [catchups, adjust]) =>
    catchups.map((catchup) => formatPaidCatchup(catchup, adjust))
)

export const getChannelCatchupProgramsFx = //
  attach({ effect: api.catchups.getChannelCatchupProgramsFx })

export const $activeChannelCatchupsPrograms = //
  api.catchups.$selectedChannelCatchupPrograms

export const $activeChannelCatchupsDatelist = combine(
  api.catchups.$selectedChannelDateList,
  i18nModel.$t,
  (channelDateList, { t }) =>
    channelDateList.map((channelDate) => formatCatchupsDate(channelDate, t))
)

reset({
  clock: [api.session.signInFx.done, api.session.signOutFx.done],
  target: api.catchups.$channelsWithCatchups,
})

// active catchup

export const setActiveCatchup = createEvent<Catchup>()

export const $activeCatchup = createStore<Catchup | null>(null) //
  .on(setActiveCatchup, (_, catchup) => catchup)
  .reset(api.session.signInFx, api.session.signOutFx)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $activeCatchup,
  key: 'tria__active-catchup',
  fail: noop,
})

export const $seletedCatchupIndex = createStore<number | null>(null)

sample({
  clock: $activeCatchup,
  source: $allCatchupsToDisplayFormatted,
  fn: (catchups, catchup) =>
    catchup && catchups.findIndex((c) => c.id === catchup.id),
  target: $seletedCatchupIndex,
})

// active date

export const setActiveDate = createEvent<number>()

export const $activeDate = createStore<number | null>(null) //
  .on(setActiveDate, (_, date) => date)

// catchup program for active date

export const setCatchupProgramsForSelectedDate = //
  createEvent<{ programs: CatchupProgram[]; catchup: Catchup }>()

export const $catchupsProgramsForSelectedDate = createStore<CatchupProgram[]>(
  []
).on(setCatchupProgramsForSelectedDate, (_, { programs, catchup }) =>
  formatProgramsForSelectedDate({ programs, catchup })
)

// get catchup programs on catchup change
sample({
  clock: $activeCatchup,
  filter: Boolean,
  fn: (catchup) => ({
    catchupId: catchup.id,
    channelId: catchup.channel.id,
  }),
  target: getChannelCatchupProgramsFx,
})

// set catchup program for active date
sample({
  clock: combine({
    programs: $activeChannelCatchupsPrograms,
    date: $activeDate,
    i18n: i18nModel.$t,
  }),
  source: $activeCatchup,
  filter: (catchup, { programs, date }) =>
    aye(catchup) && aye(date) && aye(programs),
  fn: (catchup, { programs, date }) => ({
    programs: programs[date!],
    catchup: catchup!,
  }),
  target: setCatchupProgramsForSelectedDate,
})

// select catchup program for play and navigate to player
export const selectCatchupProgram = createEvent<{
  catchup: CatchupFormatted
  program: CatchupProgramFormatted
}>()
// set catchup program for play
export const setCathchupProgram = createEvent<CatchupProgramFormatted>()

export const $selectedProgram = createStore<CatchupProgramFormatted | null>(
  null
)
  .on(selectCatchupProgram, (_, { program }) => program)
  .on(setCathchupProgram, (_, program) => program)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $selectedProgram,
  key: 'tria__selected-program',
  fail: noop,
})

sample({
  clock: selectCatchupProgram,
  filter: ({ catchup }) =>
    aye(catchup.channel.free) || aye(catchup.channel.purchaseInfo),
  fn: ({ catchup, program }) => ({
    channelId: catchup.channel.id,
    catchupId: catchup.id,
    programmeId: program.id,
  }),
  target: navigateCatchupPlayer,
})

//empty programs

export const setIsCatchupsEmpty = createEvent<boolean>()

export const $isCatchupsEmpty = restore<boolean>(setIsCatchupsEmpty, false)

sample({
  clock: getChannelCatchupProgramsFx.doneData,
  source: $activeCatchup,
  fn: (activeCatchup, clockData) =>
    !clockData.dateList.length &&
    !activeCatchup?.channel.locked &&
    !activeCatchup?.channel.blockedByAcl,
  target: setIsCatchupsEmpty,
})

sample({
  clock: getChannelCatchupProgramsFx.fail,
  source: $activeCatchup,
  fn: (activeCatchup) =>
    !activeCatchup?.channel.locked && !activeCatchup?.channel.blockedByAcl,
  target: setIsCatchupsEmpty,
})

// redirect to unlocked channel

export const setActiveUnlockedCatchups = createEvent<CatchupsRedirectParams>()

export const updateStateCatchups = createEvent<void>()

export const callCatchupsPinLockPopup = createEvent<{
  name: string
  locked: boolean
  id: number
}>()

export const $redirectToUnlockedCatchup = createStore<CatchupsRedirectParams>({
  catchupsId: 0,
  channelId: 0,
}).on(setActiveUnlockedCatchups, (_, payload) => payload)

export const $unlock = createStore(DEFAULT_UNLOCK_ID).on(
  setActiveUnlockedCatchups,
  (_, payload) => payload.channelId
)

// pipe to check if channel is currently locked
const $isLockedPipe = combine(
  pinLockFeature.$enabled,
  (pinFeatureIsOn) => (activeChannel?: CatchupFormatted | null) => {
    if (!pinFeatureIsOn) return false
    return activeChannel != null && activeChannel?.channel.locked
  }
)

sample({
  clock: setActiveUnlockedCatchups,
  source: {
    allCatchups: $allCatchupsToDisplayFormatted,
    isLocked: $isLockedPipe,
  },
  filter: ({ allCatchups, isLocked }, { channelId }) => {
    const activeChannel = allCatchups.find(
      (catchup) => catchup.channel.id === channelId
    )
    return aye(isLocked(activeChannel))
  },
  fn: ({ allCatchups }, { channelId }) => {
    const activeChannel = allCatchups.find(
      (catchup) => catchup.channel.id === channelId
    )

    return {
      name: activeChannel!.channel.formattedName ?? activeChannel!.channel.name,
      locked: activeChannel!.channel.locked,
      id: activeChannel!.channel.id,
    }
  },
  target: callCatchupsPinLockPopup,
})

sample({
  clock: pinLockFeature.$unlockedId,
  source: $redirectToUnlockedCatchup,
  filter: ({ channelId, catchupsId }, unlockId) =>
    Boolean(channelId) && Boolean(catchupsId) && unlockId === channelId,
  target: updateStateCatchups,
})

sample({
  clock: updateStateCatchups,
  source: {
    allCatchups: $allCatchupsToDisplayFormatted,
    redirect: $redirectToUnlockedCatchup,
    unlockId: $unlock,
    defaultStore: $allCatchups,
  },
  fn: ({ allCatchups, redirect, unlockId, defaultStore }) =>
    lockedFormat({ allCatchups, redirect, unlockId, defaultStore }),
  target: $allCatchupsToDisplayFormatted,
})

sample({
  clock: setActiveUnlockedCatchups,
  source: {
    allCatchups: $allCatchupsToDisplayFormatted,
    isLocked: $isLockedPipe,
  },
  filter: ({ allCatchups, isLocked }, { channelId }) => {
    const activeChannel = allCatchups.find(
      (catchup) => catchup.channel.id === channelId
    )
    return nay(isLocked(activeChannel))
  },
  target: updateStateCatchups,
})

sample({
  clock: updateStateCatchups,
  source: $redirectToUnlockedCatchup,
  filter: ({ channelId, catchupsId }) =>
    Boolean(channelId) && Boolean(catchupsId),
  fn: ({ catchupsId, channelId }) =>
    `/modules/catchups/${catchupsId}/channel/${channelId}`,
  target: router.navigatePush,
})
